import React, { useState } from 'react';
import {
  CUSTOMER_CATEGORY_LABELS,
  currentLegalVersion,
  offeredCategories,
  retainedCategoryOnIntentChange,
  type CustomerCategory,
  type UsageIntent,
} from '@lds-efsp/shared-contracts';
import { useBrand } from '../../../app/config/brand';
import { Icons } from '../../../shared/icons/Icons';
import { FormData, jobRoles, allStates } from '../../../shared/data/formData';
import {
  categoryGateBlocksAdvance,
  type RegistrationAccountMode,
  type RegistrationUsageIntent,
} from '../categoryGate';
import { handoffBlocked } from '../handoffGate';
import { FEE_WAIVER_NOTICE, showsFeeWaiverNotice } from '../feeWaiverNotice';
import { Button } from '../../../shared/components/ui/Button';
import { Input } from '../../../shared/components/ui/Input';
import { Select } from '../../../shared/components/ui/Select';
import { Card } from '../../../shared/components/ui/Card';
import { ChoiceGroup } from '../../../shared/components/ui/ChoiceGroup';
import { RadioCard } from '../../../shared/components/ui/RadioCard';
import { Logo } from '../../../shared/components/ui/Logo';
import { SectionTitle } from '../../../shared/components/ui/SectionTitle';

// Compile-time pin (SOR-147): RegistrationUsageIntent must stay exactly UsageIntent | '' in both
// directions — widening or narrowing either union makes the conditional below resolve to false,
// which violates Expect's `T extends true` constraint and fails tsc (and with it CI validate).
// The pin lives in this page module rather than in categoryGate.ts because categoryGate keeps zero imports so the SOR-129 spec property probes can compile it directly.
// The Equal helper is strict (SOR-149): it rejects `any` on either side, so an any-regression fails the pin instead of passing silently.
type Expect<T extends true> = T;
type Equal<A, B> = (<T>() => T extends A ? 1 : 2) extends (<T>() => T extends B ? 1 : 2) ? true : false;
export type RegistrationUsageIntentPin = Expect<Equal<RegistrationUsageIntent, UsageIntent | ''>>;
// @ts-expect-error -- any-regression guard: Equal<any, X> must be false; if Equal is ever weakened to a form that accepts `any` (e.g. mutual tuple-extends) this directive turns unused (TS2578) and typecheck fails.
export type RegistrationUsageIntentAnyGuard = Expect<Equal<any, UsageIntent | ''>>;

interface ProgressIndicatorProps {
  step: number;
  totalSteps: number;
  brand: { colors: { primary: string; onPrimary: string; border: string; textMuted: string } };
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ step, totalSteps, brand }) => (
  <div className="flex items-center justify-center mb-8">
    {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s, i) => (
      <React.Fragment key={s}>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all"
          style={{
            backgroundColor: step >= s ? brand.colors.primary : brand.colors.border,
            color: step >= s ? brand.colors.onPrimary : brand.colors.textMuted,
          }}
        >
          {step > s ? <Icons.Check className="w-5 h-5" /> : s}
        </div>
        {i < totalSteps - 1 && (
          <div
            className="w-20 h-1 mx-2"
            style={{ backgroundColor: step > s ? brand.colors.primary : brand.colors.border }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

interface RegistrationPageProps {
  onNavigate: (page: string) => void;
  onSignup: () => void;
}

const RegistrationPage: React.FC<RegistrationPageProps> = ({ onNavigate, onSignup }) => {
  const brand = useBrand();
  const [step, setStep] = useState(1);
  const [disclaimerStep, setDisclaimerStep] = useState(0);
  const [formData, setFormData] = useState({
    usageType: '' as UsageIntent | '',
    customerCategory: '' as CustomerCategory | '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobRole: '',
    barNumber: '',
    stateOfRecord: '',
    accountType: 'new' as RegistrationAccountMode,
    customerNumber: '',
    billingZip: '',
    firmName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [categoryError, setCategoryError] = useState(false);

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // R3: an intent change clears the category selection when it is not offered
  // under the new intent; re-selecting the current intent keeps it.
  const handleUsageTypeChange = (next: UsageIntent) => {
    setFormData(prev => ({
      ...prev,
      usageType: next,
      customerCategory: retainedCategoryOnIntentChange(next, prev.customerCategory),
    }));
    setCategoryError(false);
  };

  const handleCategorySelect = (category: CustomerCategory) => {
    setFormData(prev => ({ ...prev, customerCategory: category }));
    setCategoryError(false);
  };

  const isSelfRep = formData.usageType === 'self';
  const isFirm = formData.usageType === 'firm';

  const isStep1Valid = () => {
    if (!formData.usageType) return false;
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) return false;
    if (isFirm) {
      if (!formData.jobRole) return false;
      if (formData.jobRole === 'attorney' && (!formData.barNumber || !formData.stateOfRecord)) return false;
      if (formData.accountType === 'join' && (!formData.customerNumber || !formData.billingZip)) return false;
    }
    return true;
  };

  const isStep2Valid = () => {
    if (isFirm && !formData.firmName) return false;
    if (!formData.address1 || !formData.city || !formData.state || !formData.zip) return false;
    return true;
  };

  const handleNext = () => {
    if (step === 1) {
      // R4: the gate sits in the advance handler (not isStep1Valid) so the
      // attempt happens and the message shows — and it fires before the
      // self-rep disclaimer sequence.
      if (categoryGateBlocksAdvance(formData.usageType, formData.accountType, formData.customerCategory)) {
        setCategoryError(true);
        return;
      }
      if (isSelfRep) {
        setDisclaimerStep(1);
      } else {
        setStep(2);
      }
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handleDisclaimerNext = () => {
    if (disclaimerStep < 4) {
      setDisclaimerStep(disclaimerStep + 1);
    } else {
      setDisclaimerStep(0);
      setStep(2);
    }
  };

  const handleDisclaimerBack = () => {
    if (disclaimerStep > 1) {
      setDisclaimerStep(disclaimerStep - 1);
    } else {
      setDisclaimerStep(0);
    }
  };

  // R5/R8 belt: the step-1 gate already blocks category-less create-new
  // advancement; guarding the handoff with the full extracted gate (ToS +
  // Privacy acceptances plus the category belt) covers any future step-flow
  // change, so a handed-off payload can never lack a category or either
  // acceptance. handoffBlocked is the same boolean the button's disabled prop
  // previously spelled inline.
  const createAccountBlocked = handoffBlocked({
    acceptTerms,
    acceptPrivacy,
    usageType: formData.usageType,
    accountType: formData.accountType,
    selectedCategory: formData.customerCategory,
  });

  const handleCreateAccount = () => {
    if (createAccountBlocked) return;
    // Legal acceptance evidence (SOR-37): pin the terms/privacy versions the
    // registrant just agreed to, so the account records exactly what was shown
    // at signup (the hosted-signup round-trip re-enters through onboarding,
    // which forwards these into provisioning). Accessibility is never recorded.
    const acceptedAt = new Date().toISOString();
    const pending = {
      ...formData,
      termsVersionAccepted: currentLegalVersion('terms', acceptedAt)?.version,
      privacyVersionAccepted: currentLegalVersion('privacy', acceptedAt)?.version,
    };
    localStorage.setItem('pendingRegistration', JSON.stringify(pending));
    onSignup();
  };

  // Disclaimer screens for self-represented litigants
  if (disclaimerStep > 0) {
    const disclaimerContent = [
      null,
      {
        title: `Nice to meet you, ${formData.firstName}!`,
        content: (
          <div className="space-y-4">
            <p>Before we continue, please be aware of the following:</p>
            <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: brand.colors.textLight }}>
              <li>We are not licensed attorneys and cannot offer legal advice</li>
              <li>We do not prepare legal documents</li>
              <li>Live support is not available for self-represented litigants</li>
            </ul>
          </div>
        ),
      },
      {
        title: `${formData.firstName}, did you know...`,
        content: (
          <div className="space-y-4">
            <p>There are resources available to help self-represented litigants navigate the legal system.</p>
            <a href="#" style={{ color: brand.colors.link }} className="font-medium block">
              View Self-Help Resources
            </a>
          </div>
        ),
      },
      {
        title: 'For all your legal questions, please contact an attorney',
        content: (
          <div className="space-y-4">
            <p className="text-sm" style={{ color: brand.colors.textLight }}>
              {brand.company.name} provides document filing and service of process. We are not a law firm and cannot provide legal advice, explain legal procedures, or answer legal questions.
            </p>
            <p className="text-sm" style={{ color: brand.colors.textLight }}>
              If you need legal assistance, please consult with a licensed attorney in your jurisdiction.
            </p>
          </div>
        ),
      },
      {
        title: `${formData.firstName}, still want to create a ${brand.company.name} account?`,
        content: (
          <div className="space-y-4">
            <p>By continuing, you acknowledge that:</p>
            <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: brand.colors.textLight }}>
              <li>You understand that {brand.company.name} cannot provide legal advice</li>
              <li>You are responsible for the accuracy and completeness of your documents</li>
              <li>Live support options may be limited for self-represented litigants</li>
            </ul>
          </div>
        ),
      },
    ];

    const current = disclaimerContent[disclaimerStep];

    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: brand.colors.bgLight }}>
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <Logo />
          </div>
          <Card>
            <h1 className="text-3xl font-semibold mb-6 text-center" style={{ color: brand.colors.secondary }}>
              Create your account
            </h1>
            <h2 className="text-2xl font-semibold mb-6 text-center" style={{ color: brand.colors.secondary }}>
              {current.title}
            </h2>
            <div className="mb-8" style={{ color: brand.colors.text }}>
              {current.content}
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={handleDisclaimerBack}>
                Back
              </Button>
              <Button onClick={handleDisclaimerNext}>
                {disclaimerStep === 4 ? 'Yes, Continue' : 'Continue'}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: brand.colors.bgLight }}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Logo />
        </div>

        <h1 className="text-3xl font-semibold mb-6 text-center" style={{ color: brand.colors.secondary }}>
          Create your account
        </h1>

        <ProgressIndicator step={step} totalSteps={3} brand={brand} />

        {/* Step 1: Account Setup */}
        {step === 1 && (
          <Card>
            <h2 className="text-2xl font-semibold mb-6" style={{ color: brand.colors.secondary }}>
              Account Setup
            </h2>

            <ChoiceGroup required question={`How do you plan to use ${brand.company.name}?`} className="mb-6">
              <div className="space-y-3">
                <RadioCard
                  name="usageType"
                  label="Law Firm / Organization"
                  description="To manage litigation tasks at a law firm or organization"
                  checked={formData.usageType === 'firm'}
                  onChange={() => handleUsageTypeChange('firm')}
                />
                <RadioCard
                  name="usageType"
                  label="Self-Represented Litigant"
                  description="To manage litigation tasks for myself (I am representing myself in a court case)"
                  checked={formData.usageType === 'self'}
                  onChange={() => handleUsageTypeChange('self')}
                />
              </div>
            </ChoiceGroup>

            {/* Customer Category selector (SOR-129): rendered entirely from the
                shared-contracts vocabulary — values, labels, order, partition.
                Distinct from the new-vs-join "Account Type" control below. */}
            {formData.usageType && (
              <div className="mb-6">
                <ChoiceGroup
                  required
                  question="Customer Category"
                  className="space-y-3"
                  data-testid="customer-category-group"
                >
                  {offeredCategories(formData.usageType).map((category) => (
                    <RadioCard
                      key={category}
                      name="customerCategory"
                      label={CUSTOMER_CATEGORY_LABELS[category]}
                      description=""
                      checked={formData.customerCategory === category}
                      onChange={() => handleCategorySelect(category)}
                    />
                  ))}
                </ChoiceGroup>
                {categoryError && (
                  <p
                    role="alert"
                    data-testid="customer-category-error"
                    className="text-sm mt-2"
                    style={{ color: brand.colors.error }}
                  >
                    Please select a customer category to continue.
                  </p>
                )}
              </div>
            )}

            <SectionTitle>Personal Information</SectionTitle>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Input label="First Name" required value={formData.firstName} onChange={(e) => updateField('firstName', e.target.value)} />
              <Input label="Last Name" required value={formData.lastName} onChange={(e) => updateField('lastName', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Input label="Email" required type="email" value={formData.email} onChange={(e) => updateField('email', e.target.value)} />
              <Input label="Phone" required type="tel" value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} />
            </div>

            {isFirm && (
              <>
                <div className="mb-4">
                  <Select label="Job Role" required options={jobRoles} value={formData.jobRole} onChange={(e) => updateField('jobRole', e.target.value)} />
                </div>
                {formData.jobRole === 'attorney' && (
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <Input label="Bar Number" required value={formData.barNumber} onChange={(e) => updateField('barNumber', e.target.value)} />
                    <Select label="State of Record" required options={allStates} value={formData.stateOfRecord} onChange={(e) => updateField('stateOfRecord', e.target.value)} />
                  </div>
                )}
                <ChoiceGroup question="Account Type" className="mb-6">
                  <div className="space-y-3">
                    <RadioCard name="accountType" label="Create new account" description={`For firms that have never used ${brand.company.name} before`} checked={formData.accountType === 'new'} onChange={() => updateField('accountType', 'new')} />
                    <RadioCard name="accountType" label="Join Firm Account" description="Join an existing firm account (requires Customer Number and Billing ZIP)" checked={formData.accountType === 'join'} onChange={() => updateField('accountType', 'join')} />
                  </div>
                </ChoiceGroup>
                {formData.accountType === 'join' && (
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <Input label="Customer Number" required value={formData.customerNumber} onChange={(e) => updateField('customerNumber', e.target.value)} />
                    <Input label="Billing ZIP Code" required value={formData.billingZip} onChange={(e) => updateField('billingZip', e.target.value)} />
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between pt-4 border-t" style={{ borderColor: brand.colors.border }}>
              <Button variant="ghost" onClick={() => onNavigate('login')}>Back to Login</Button>
              <Button onClick={handleNext} disabled={!isStep1Valid()}>Continue</Button>
            </div>
          </Card>
        )}

        {/* Step 2: Billing Info */}
        {step === 2 && (
          <Card>
            <h2 className="text-2xl font-semibold mb-6" style={{ color: brand.colors.secondary }}>
              {isFirm ? 'Firm & Billing Information' : 'Billing Information'}
            </h2>
            {isFirm && (
              <Input label="Law firm or organization" required className="mb-4" value={formData.firmName} onChange={(e) => updateField('firmName', e.target.value)} />
            )}
            <Input label="Billing address" required className="mb-4" value={formData.address1} onChange={(e) => updateField('address1', e.target.value)} />
            <Input label="Billing address line 2" className="mb-4" value={formData.address2} onChange={(e) => updateField('address2', e.target.value)} />
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Input label="City" required value={formData.city} onChange={(e) => updateField('city', e.target.value)} />
              <Select label="State" required options={FormData.states} value={formData.state} onChange={(e) => updateField('state', e.target.value)} />
            </div>
            <Input label="Zip code" required className="mb-6" value={formData.zip} onChange={(e) => updateField('zip', e.target.value)} />
            <div className="flex justify-between pt-4 border-t" style={{ borderColor: brand.colors.border }}>
              <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={handleNext} disabled={!isStep2Valid()}>Continue</Button>
            </div>
          </Card>
        )}

        {/* Step 3: Review & Create Account */}
        {step === 3 && (
          <Card>
            <h2 className="text-2xl font-semibold mb-6" style={{ color: brand.colors.secondary }}>Review Your Information</h2>
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-medium" style={{ color: brand.colors.secondary }}>Name:</span>{' '}<span style={{ color: brand.colors.text }}>{formData.firstName} {formData.lastName}</span></div>
                <div><span className="font-medium" style={{ color: brand.colors.secondary }}>Email:</span>{' '}<span style={{ color: brand.colors.text }}>{formData.email}</span></div>
                <div><span className="font-medium" style={{ color: brand.colors.secondary }}>Phone:</span>{' '}<span style={{ color: brand.colors.text }}>{formData.phone}</span></div>
                {isFirm && (
                  <>
                    <div><span className="font-medium" style={{ color: brand.colors.secondary }}>Firm:</span>{' '}<span style={{ color: brand.colors.text }}>{formData.firmName}</span></div>
                    <div><span className="font-medium" style={{ color: brand.colors.secondary }}>Job Role:</span>{' '}<span style={{ color: brand.colors.text }}>{jobRoles.find(r => r.value === formData.jobRole)?.label || formData.jobRole}</span></div>
                    {formData.jobRole === 'attorney' && (
                      <div><span className="font-medium" style={{ color: brand.colors.secondary }}>Bar Number:</span>{' '}<span style={{ color: brand.colors.text }}>{formData.barNumber}</span></div>
                    )}
                  </>
                )}
              </div>
              <div className="text-sm pt-4 border-t" style={{ borderColor: brand.colors.border }}>
                <span className="font-medium" style={{ color: brand.colors.secondary }}>Address:</span>
                <div style={{ color: brand.colors.text }}>
                  {formData.address1}{formData.address2 && `, ${formData.address2}`}<br />
                  {formData.city}, {formData.state} {formData.zip}
                </div>
              </div>
            </div>
            {/* Fee-waiver notice (SOR-129 R6/D4): rendered from the single
                content constant iff the selected category is eFiling Fee
                Waiver (Pro Per) — no payment step exists, so it lives here. */}
            {showsFeeWaiverNotice(step, formData.customerCategory) && (
              <div
                data-testid="fee-waiver-notice"
                className="rounded-lg border p-4 mb-6 text-sm"
                style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight }}
              >
                <p className="font-medium mb-2" style={{ color: brand.colors.secondary }}>
                  {FEE_WAIVER_NOTICE.heading}
                </p>
                <p className="mb-2" style={{ color: brand.colors.text }}>{FEE_WAIVER_NOTICE.body}</p>
                <p style={{ color: brand.colors.textLight }}>{FEE_WAIVER_NOTICE.qualifications}</p>
              </div>
            )}
            <div className="border-t pt-6 space-y-4" style={{ borderColor: brand.colors.border }}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} style={{ accentColor: brand.colors.primary }} />
                <span className="text-sm" style={{ color: brand.colors.text }}>I agree to the{' '}<a href="/legal/terms" target="_blank" rel="noopener noreferrer" style={{ color: brand.colors.link }} className="underline" data-testid="registration-terms-link">Terms of Service</a></span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} style={{ accentColor: brand.colors.primary }} />
                <span className="text-sm" style={{ color: brand.colors.text }}>I agree to the{' '}<a href="/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: brand.colors.link }} className="underline" data-testid="registration-privacy-link">Privacy Policy</a></span>
              </label>
            </div>
            <div className="flex justify-between pt-6 border-t mt-6" style={{ borderColor: brand.colors.border }}>
              <Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={handleCreateAccount} disabled={createAccountBlocked}>Create Account</Button>
            </div>
          </Card>
        )}

        <p className="text-center text-sm mt-6" style={{ color: brand.colors.textLight }}>
          Already have an account?{' '}
          <button onClick={() => onNavigate('login')} style={{ color: brand.colors.link }} className="font-medium bg-transparent border-none cursor-pointer">Log in</button>
        </p>
      </div>
    </div>
  );
};

export default RegistrationPage;
